import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["viewport", "canvas", "item", "axisInput", "viewInput", "cardPanel", "listPanel", "mapPanel", "mapViewport", "mapCanvas", "toolbarShell"]
  static values = { preserveScroll: Boolean }

  connect() {
    this.mode = "y"
    this.viewMode = "cards"
    this.mapScale = 1
    this.mapX = 0
    this.mapY = 0
    this.mapDragging = false
    this.layoutReady = false
    this.resizeHandler = this.throttle(() => this.layoutSpatial(), 150)
    this.scrollHandler = this.throttle(() => this.updateVirtualWindow(), 80)
    this.toolbarScrollHandler = this.updateToolbarState.bind(this)
    this.toolbarTicking = false
    this.toolbarManuallyExpanded = false
    this.toolbarExpandedAtScrollY = 0
    this.toolbarExpandedAtViewportScroll = 0
    this.mapMoveHandler = (event) => this.mapPointerMove(event)
    this.mapUpHandler = () => this.mapPointerUp()
    window.addEventListener("resize", this.resizeHandler)
    window.addEventListener("pointermove", this.mapMoveHandler)
    window.addEventListener("pointerup", this.mapUpHandler)
    window.addEventListener("scroll", this.toolbarScrollHandler, { passive: true })

    if (this.hasViewportTarget) {
      this.viewportTarget.addEventListener("scroll", this.scrollHandler, { passive: true })
      this.viewportTarget.addEventListener("scroll", this.toolbarScrollHandler, { passive: true })
    }

    this.applyMode()
    this.updateToolbarState()
  }

  disconnect() {
    if (this.revealFrame) cancelAnimationFrame(this.revealFrame)
    window.removeEventListener("resize", this.resizeHandler)
    window.removeEventListener("pointermove", this.mapMoveHandler)
    window.removeEventListener("pointerup", this.mapUpHandler)
    window.removeEventListener("scroll", this.toolbarScrollHandler)
    window.cancelAnimationFrame(this.toolbarFrame)
    this.toolbarTicking = false
    document.body.classList.remove("company-spatial-active")
    document.body.classList.remove("company-map-active")

    if (this.hasViewportTarget) {
      this.viewportTarget.removeEventListener("scroll", this.scrollHandler)
      this.viewportTarget.removeEventListener("scroll", this.toolbarScrollHandler)
    }
  }

  updateToolbarState() {
    if (!this.hasToolbarShellTarget || this.toolbarTicking) return

    this.toolbarTicking = true
    this.toolbarFrame = window.requestAnimationFrame(() => {
      const viewportScroll = this.hasViewportTarget ? this.viewportTarget.scrollTop : 0
      if (
        this.toolbarManuallyExpanded &&
        (Math.abs(window.scrollY - this.toolbarExpandedAtScrollY) > 8 ||
          Math.abs(viewportScroll - this.toolbarExpandedAtViewportScroll) > 8)
      ) {
        this.toolbarManuallyExpanded = false
      }

      this.toolbarShellTarget.classList.toggle(
        "is-compact",
        !this.toolbarManuallyExpanded && (window.scrollY >= 40 || viewportScroll >= 40)
      )
      this.toolbarTicking = false
    })
  }

  expandToolbar() {
    if (!this.hasToolbarShellTarget || !this.toolbarShellTarget.classList.contains("is-compact")) return

    this.toolbarManuallyExpanded = true
    this.toolbarExpandedAtScrollY = window.scrollY
    this.toolbarExpandedAtViewportScroll = this.hasViewportTarget ? this.viewportTarget.scrollTop : 0
    this.toolbarShellTarget.classList.remove("is-compact")
  }

  axisChanged(event) {
    if (!event.target.checked) return

    this.mode = event.target.value
    this.applyMode()
  }

  viewChanged(event) {
    if (!event.target.checked) return

    const scrollTop = window.scrollY
    this.viewMode = event.target.value
    this.applyMode()
    this.revealViewPanel(scrollTop)
  }

  entriesAppended() {
    if (this.viewMode === "cards" && this.mode === "xy") {
      this.layoutSpatial()
      this.updateVirtualWindow()
    }
  }

  setAxis(event) {
    const axis = event.params.axis
    if (!axis) return

    this.axisInputTargets.forEach((input) => {
      input.checked = input.value === axis
    })
    this.mode = axis
    this.applyMode()
  }

  setView(event) {
    const view = event.params.view
    if (!view) return
    event.preventDefault()
    const scrollTop = window.scrollY

    this.viewInputTargets.forEach((input) => {
      input.checked = input.value === view
    })
    this.viewMode = view
    this.applyMode()
    this.revealViewPanel(scrollTop)
  }

  revealViewPanel(scrollTop) {
    if (this.revealFrame) cancelAnimationFrame(this.revealFrame)
    const preservePosition = this.preserveScrollValue || this.viewMode !== "map"
    if (preservePosition) window.scrollTo(0, scrollTop)

    this.revealFrame = requestAnimationFrame(() => {
      if (preservePosition) {
        window.scrollTo(0, scrollTop)
        this.revealFrame = null
        return
      }
      const panel = {
        cards: this.hasCardPanelTarget ? this.cardPanelTarget : null,
        list: this.hasListPanelTarget ? this.listPanelTarget : null,
        map: this.hasMapPanelTarget ? this.mapPanelTarget : null
      }[this.viewMode]

      this.revealFrame = null
      if (!panel) return

      const panelTop = window.scrollY + panel.getBoundingClientRect().top
      window.scrollTo({ top: Math.max(0, panelTop - 96), behavior: "smooth" })
    })
  }

  applyMode() {
    this.applyViewPanels()

    if (this.viewMode !== "cards") {
      this.disableSpatialAxis()
      if (this.hasViewportTarget && this.hasCanvasTarget) this.resetSpatialMode()
      if (this.viewMode === "map") {
        this.activateMap()
      } else {
        this.deactivateMap()
      }
      return
    }

    this.deactivateMap()
    this.enableSpatialAxis()

    if (!this.hasViewportTarget || !this.hasCanvasTarget) return

    if (this.mode === "xy") {
      document.body.classList.add("company-spatial-active")
      this.viewportTarget.classList.add("is-spatial")
      this.canvasTarget.classList.add("is-spatial")
      this.layoutSpatial()
      this.centerViewport()
      this.updateVirtualWindow()
      return
    }

    this.resetSpatialMode()
  }

  applyViewPanels() {
    if (this.hasCardPanelTarget) this.cardPanelTarget.classList.toggle("hidden", this.viewMode !== "cards")
    if (this.hasListPanelTarget) this.listPanelTarget.classList.toggle("hidden", this.viewMode !== "list")
    if (this.hasMapPanelTarget) this.mapPanelTarget.classList.toggle("hidden", this.viewMode !== "map")
  }

  disableSpatialAxis() {
    this.axisInputTargets.forEach((input) => {
      input.disabled = true
      input.checked = input.value === "y"
    })
    this.mode = "y"
  }

  enableSpatialAxis() {
    this.axisInputTargets.forEach((input) => {
      input.disabled = false
    })
  }

  resetSpatialMode() {
    document.body.classList.remove("company-spatial-active")
    this.viewportTarget.classList.remove("is-spatial")
    this.canvasTarget.classList.remove("is-spatial")
    this.canvasTarget.removeAttribute("style")
    this.itemTargets.forEach((item) => {
      item.classList.remove("is-virtual-hidden", "is-virtual-visible")
      item.removeAttribute("style")
    })
    this.layoutReady = false
  }

  activateMap() {
    document.body.classList.add("company-map-active")
    if (this.hasMapPanelTarget) this.mapPanelTarget.classList.add("is-active")
    this.element.dispatchEvent(new CustomEvent("webuild:map-requested", { bubbles: true }))
    if (this.hasMapPanelTarget && window.matchMedia("(max-width: 767px)").matches) {
      requestAnimationFrame(() => {
        this.mapPanelTarget.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    }
  }

  deactivateMap() {
    document.body.classList.remove("company-map-active")
    if (this.hasMapPanelTarget) this.mapPanelTarget.classList.remove("is-active")
    this.mapDragging = false
  }

  centerMap() {
    if (!this.hasMapViewportTarget || !this.hasMapCanvasTarget) return

    const viewport = this.mapViewportTarget.getBoundingClientRect()
    this.mapScale = 1
    this.mapX = (viewport.width - this.mapCanvasTarget.offsetWidth) / 2
    this.mapY = (viewport.height - this.mapCanvasTarget.offsetHeight) / 2
    this.applyMapTransform()
  }

  mapWheel(event) {
    if (this.viewMode !== "map" || !this.hasMapCanvasTarget) return

    event.preventDefault()
    const rect = this.mapViewportTarget.getBoundingClientRect()
    const pointX = event.clientX - rect.left
    const pointY = event.clientY - rect.top
    const beforeX = (pointX - this.mapX) / this.mapScale
    const beforeY = (pointY - this.mapY) / this.mapScale
    const nextScale = Math.min(2.8, Math.max(0.55, this.mapScale * (event.deltaY < 0 ? 1.12 : 0.88)))

    this.mapScale = nextScale
    this.mapX = pointX - beforeX * this.mapScale
    this.mapY = pointY - beforeY * this.mapScale
    this.applyMapTransform()
  }

  mapPointerDown(event) {
    if (this.viewMode !== "map") return

    this.mapDragging = true
    this.mapStartX = event.clientX
    this.mapStartY = event.clientY
    this.mapOriginX = this.mapX
    this.mapOriginY = this.mapY
  }

  mapPointerMove(event) {
    if (!this.mapDragging) return

    this.mapX = this.mapOriginX + event.clientX - this.mapStartX
    this.mapY = this.mapOriginY + event.clientY - this.mapStartY
    this.applyMapTransform()
  }

  mapPointerUp() {
    this.mapDragging = false
  }

  zoomMap(event) {
    const direction = event.params.direction || 1
    const syntheticDelta = direction > 0 ? -1 : 1
    const rect = this.mapViewportTarget.getBoundingClientRect()
    this.mapWheel({
      preventDefault() {},
      deltaY: syntheticDelta,
      clientX: rect.left + rect.width / 2,
      clientY: rect.top + rect.height / 2
    })
  }

  applyMapTransform() {
    if (!this.hasMapCanvasTarget) return

    this.mapCanvasTarget.style.transform = `translate3d(${this.mapX}px, ${this.mapY}px, 0) scale(${this.mapScale})`
  }

  layoutSpatial() {
    if (this.mode !== "xy" || !this.hasCanvasTarget || this.itemTargets.length === 0) return

    const itemSize = Math.min(408, Math.max(300, window.innerWidth - 32))
    const gapX = Math.round(itemSize * 0.14)
    const gapY = gapX
    const columns = Math.max(5, Math.ceil(Math.sqrt(this.itemTargets.length) * 1.18))
    const rows = Math.ceil(this.itemTargets.length / columns)
    const staggerY = Math.round((itemSize + gapY) * 0.5)
    const canvasWidth = Math.max(window.innerWidth * 2.4, columns * (itemSize + gapX))
    const canvasHeight = Math.max(window.innerHeight * 2.4, rows * (itemSize + gapY) + staggerY)
    const centerX = canvasWidth / 2
    const centerY = canvasHeight / 2
    const stepX = itemSize + gapX
    const stepY = itemSize + gapY
    const gridWidth = columns * itemSize + (columns - 1) * gapX
    const gridHeight = rows * itemSize + Math.max(0, rows - 1) * gapY + staggerY
    const originX = centerX - gridWidth / 2
    const originY = centerY - gridHeight / 2

    this.canvasTarget.style.width = `${canvasWidth}px`
    this.canvasTarget.style.height = `${canvasHeight}px`

    this.itemTargets.forEach((item, index) => {
      const column = index % columns
      const row = Math.floor(index / columns)
      const left = Math.round(originX + column * stepX)
      const top = Math.round(originY + row * stepY + (column % 2 === 1 ? staggerY : 0))

      item.style.width = `${itemSize}px`
      item.style.height = `${itemSize}px`
      item.style.left = `${left}px`
      item.style.top = `${top}px`
      item.style.transform = "translate3d(0, 0, 0)"
      item.dataset.spatialX = left + itemSize / 2
      item.dataset.spatialY = top + itemSize / 2
    })

    this.layoutReady = true
  }

  centerViewport() {
    if (this.layoutReady) {
      const x = (this.canvasTarget.scrollWidth - this.viewportTarget.clientWidth) / 2
      const y = Math.max(0, (this.canvasTarget.scrollHeight - this.viewportTarget.clientHeight) / 2 - 200)
      this.viewportTarget.scrollTo({ left: x, top: y, behavior: "auto" })
    }
  }

  updateVirtualWindow() {
    if (this.mode !== "xy" || !this.layoutReady) return

    const buffer = Math.max(this.viewportTarget.clientWidth, this.viewportTarget.clientHeight) * 0.8
    const bounds = {
      left: this.viewportTarget.scrollLeft - buffer,
      top: this.viewportTarget.scrollTop - buffer,
      right: this.viewportTarget.scrollLeft + this.viewportTarget.clientWidth + buffer,
      bottom: this.viewportTarget.scrollTop + this.viewportTarget.clientHeight + buffer
    }

    this.itemTargets.forEach((item) => {
      const x = Number(item.dataset.spatialX)
      const y = Number(item.dataset.spatialY)
      const visible = x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom

      item.classList.toggle("is-virtual-visible", visible)
      item.classList.toggle("is-virtual-hidden", !visible)
    })
  }

  spiralPoint(index) {
    if (index === 0) return { x: 0, y: 0 }

    const ring = Math.ceil((Math.sqrt(index + 1) - 1) / 2)
    const side = ring * 2
    const max = (ring * 2 + 1) ** 2 - 1
    const offset = max - index

    if (offset < side) return { x: ring - offset, y: -ring }
    if (offset < side * 2) return { x: -ring, y: -ring + (offset - side) }
    if (offset < side * 3) return { x: -ring + (offset - side * 2), y: ring }

    return { x: ring, y: ring - (offset - side * 3) }
  }

  throttle(callback, delay) {
    let frame = null
    let lastRun = 0

    return (...args) => {
      const now = Date.now()

      if (now - lastRun >= delay) {
        lastRun = now
        callback(...args)
        return
      }

      if (frame) return

      frame = window.setTimeout(() => {
        frame = null
        lastRun = Date.now()
        callback(...args)
      }, delay - (now - lastRun))
    }
  }
}
